import { useEffect, useState, Suspense } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { InView } from 'react-intersection-observer'
import cn from 'classnames'
import { ToastContainer } from 'react-toastify'
// Layout
import Footer from '@/components/newFooter'
import Header from '@/components/newHeader/Header'
import SignupModal from '@/components/signupModal'
import Loader from '@/components/newLoader/Loader'
// Puppy Profile - Critical components
import Datasheet from '@/components/puppyProfile/Datasheet'
import PetImage from '@/components/puppyProfile/PetImage'
import Keypad from '@/components/puppyProfile/Keypad'
import MobileKeypad from '@/components/puppyProfile/MobileKeypad'
import Icons from '@/components/puppyProfile/lib/icons'

// Lazy loaded components for better performance
const AboutBreed = dynamic(() => import('@/components/puppyProfile/AboutBreed'), {
  ssr: false,
  loading: () => (
    <div className={styles.sectionLoading}>
      <Loader theme="short" />
    </div>
  ),
})
const Breeder = dynamic(() => import('@/components/puppyProfile/Breeder'), {
  ssr: false,
  loading: () => (
    <div className={styles.sectionLoading}>
      <Loader theme="short" />
    </div>
  ),
})
const Certificates = dynamic(() => import('@/components/puppyProfile/Certificates'), {
  ssr: false,
  loading: () => (
    <div className={styles.sectionLoading}>
      <Loader theme="short" />
    </div>
  ),
})
const FinancingModal = dynamic(() => import('@/components/puppyProfile/FinancingModal'), {
  ssr: false,
})
const HealthGuarantee = dynamic(() => import('@/components/puppyProfile/HealthGuarantee'), {
  ssr: false,
  loading: () => (
    <div className={styles.sectionLoading}>
      <Loader theme="short" />
    </div>
  ),
})
const Parents = dynamic(() => import('@/components/puppyProfile/Parents'), {
  ssr: false,
  loading: () => (
    <div className={styles.sectionLoading}>
      <Loader theme="short" />
    </div>
  ),
})
const OtherDogs = dynamic(() => import('@/components/puppyProfile/OtherDogs'), {
  ssr: false,
  loading: () => (
    <div className={styles.otherDogsLoading}>
      <Loader theme="tall" />
    </div>
  ),
})
const Shipment = dynamic(() => import('@/components/puppyProfile/Shipment'), {
  ssr: false,
  loading: () => (
    <div className={styles.sectionLoading}>
      <Loader theme="short" />
    </div>
  ),
})
const Why = dynamic(() => import('@/components/puppyProfile/Why'), {
  ssr: false,
  loading: () => (
    <div className={styles.sectionLoading}>
      <Loader theme="short" />
    </div>
  ),
})
import BasicHeadSeo from '@/components/basicHeadSeo'
import Skeleton from '@/components/puppyProfile/Skeleton'
// Utils
import { convertGender } from '@/components/puppyProfile/lib/utils'
import useMediaQuery from '@/utils/publicApi'
import { initializeApollo, getNoTrackUser } from '@utils'
import { Dog, IDogDetailData, IGetFavouriteDogCount } from '@/types'
import { useFavourites } from '@/hooks/useFavourites'
// GQL Queries
import {
  CREATE_DOGVISIT,
  GET_BREED_DETAILS,
  GET_DOG_DETAIL,
  GET_FAV_DOG,
  PUPPY_SEARCH,
} from '@graphql'

import { ROBOTS_META } from 'const'
import styles from './[id].module.scss'

export const config = { unstable_runtimeJS: true }

interface IPuppyProfile {
  isDiscountBannerClosed: boolean
  // eslint-disable-next-line no-unused-vars
  setIsDiscountBannerClosed: (closed: boolean) => void
  dataDog: {
    dog: Dog
  }
  breedDetails: any
  allBreedPuppies: any
}

export default function PuppyProfile({
  isDiscountBannerClosed,
  setIsDiscountBannerClosed,
  dataDog,
  breedDetails,
  allBreedPuppies,
}: IPuppyProfile) {
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width:600px)')
  const isTablet = useMediaQuery('(max-width:1270px)')

  const data = dataDog
  const dogId = dataDog?.dog?.id
  const loading = router.isFallback
  const dog = data?.dog || ({} as Dog)
  const salePrice = dog?.salePrice?.subTotal || 0

  const { favourites, userData, selectedDog, toggleFavourite, isFavourite, refreshUserData } =
    useFavourites()

  // Defer non-critical queries to avoid waterfalls
  const { data: dataFav } = useQuery<IGetFavouriteDogCount>(GET_FAV_DOG, {
    variables: { id: dogId },
    skip: !dogId,
    // Use cache-first to avoid unnecessary network requests
    fetchPolicy: 'cache-first',
  })

  const favs = dataFav?.favouriteDogCount?.count

  // Only fetch updated data if we have the basic dog info
  const { data: dataApp, loading: loadingData } = useQuery<IDogDetailData>(GET_DOG_DETAIL, {
    variables: { selected: dogId, slug: data?.dog?.slug },
    skip: !dogId || !data?.dog?.slug,
    fetchPolicy: 'cache-first',
  })

  const infoUpdated = dataApp?.dog

  // Dog Visit - set dog id to dog visit table
  const [createDogVisit] = useMutation(CREATE_DOGVISIT)

  useEffect(() => {
    const noTackUser = getNoTrackUser()
    // create dog view count if the user is not a breeder or a admin
    if (!noTackUser) {
      createDogVisit({ variables: { dog: dog?.id } })
    }
  }, [dog.id])

  const [visibility, setVisibility] = useState({
    showSignup: false,
    showFinancingModal: false,
    isPetImageVisible: true,
    isKeypadVisible: true,
  })

  let gender = convertGender(dog?.gender)
  if (Array.isArray(gender)) gender = gender.join(' ')
  const remainingBreeds = allBreedPuppies?.search?.rows.filter((elem) => elem.id !== dogId)
  const breedDogs = {
    id: dog?.breed?.id,
    name: dog?.breed?.name,
    slug: dog?.breed?.slug,
    dogs: remainingBreeds?.slice(0, 10),
  }

  let hasApplication, dataApplication

  // Handle Application data
  if (infoUpdated) {
    hasApplication = infoUpdated.dogApplications.filter(
      (elem) => elem.customerId === userData?.id && !elem.canceled
    )
    dataApplication = hasApplication?.[0]
  }

  // Is favorite?
  const handleUpdateFavourite = () => {
    const success = toggleFavourite(dog?.id)
    if (!success) {
      setVisibility((prevState) => ({ ...prevState, showSignup: true }))
    }
  }

  return (
    <>
      <BasicHeadSeo
        title={`${dog?.name} | ${dog?.breed?.name} ${convertGender(
          dog?.gender
        )} | Puppy ID ${dog.id}`}
        description={dog?.description}
        url={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppy/${dog?.slug}`}
        image={`${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${dog?.profilePicture?.medium}`}
        content={ROBOTS_META}
      />

      <main className={styles.main}>
        <ToastContainer
          position="bottom-left"
          autoClose={false}
          closeButton={false}
          closeOnClick={false}
          limit={1}
        />

        <Header
          isDiscountBannerClosed={isDiscountBannerClosed}
          setIsDiscountBannerClosed={setIsDiscountBannerClosed}
        />

        {!visibility.isKeypadVisible && (
          <MobileKeypad
            dog={dog}
            salePrice={salePrice}
            loading={loading}
            dataApplication={dataApplication}
          />
        )}

        <div className={styles.content}>
          {loading || loadingData ? (
            <Skeleton />
          ) : (
            <>
              <div className={dog.isAvailable ? styles.paginationHead : styles.paginationHeadTop}>
                <p className={styles.steps}>
                  <a
                    className={styles.step}
                    href={`${process.env.NEXT_PUBLIC_ROOT_URL}/search?sort=by-featured`}
                  >
                    All Puppies{' '}
                  </a>
                  <span className={styles.arrowRight} />{' '}
                  <a
                    className={styles.step}
                    href={`${process.env.NEXT_PUBLIC_ROOT_URL}/search?breed=${dog?.breed?.slug}`}
                  >
                    {' '}
                    {dog?.breed?.name}
                  </a>{' '}
                  <span className={styles.arrowRight} />{' '}
                  <a className={styles.step}>{dog?.name}</a>{' '}
                </p>

                <Icons {...{ dog }} />
              </div>

              {isMobile && (
                <button
                  onClick={() => window.history.go(-1)}
                  className={cn({
                    [styles.btnBack]: true,
                    [styles.hasBanner]: !dog?.isAvailable,
                  })}
                >
                  <img
                    className={styles.arrowLeftBack}
                    src="/puppy/arrow-left-black-large.svg"
                    alt="arrow left"
                  />
                </button>
              )}

              <Suspense
                fallback={
                  <div className={styles.imageLoading}>
                    <Loader theme="short" />
                  </div>
                }
              >
                <InView
                  onChange={(inView) =>
                    setVisibility((curr) => ({
                      ...curr,
                      isPetImageVisible: inView,
                    }))
                  }
                >
                  <PetImage dog={dog} isMobile={isMobile} />
                </InView>
              </Suspense>

              <div className={styles.detail}>
                <div className={styles.leftPane}>
                  <Suspense
                    fallback={
                      <div className={styles.datasheetLoading}>
                        <Loader theme="short" />
                      </div>
                    }
                  >
                    <InView
                      onChange={(inView) =>
                        setVisibility((curr) => ({
                          ...curr,
                          isKeypadVisible: inView,
                        }))
                      }
                    >
                      <Datasheet
                        {...{
                          isMobile,
                          dog,
                          favs,
                          breedDetails,
                          salePrice,
                          dataApplication,
                        }}
                        isFavourite={isFavourite(dog?.id)}
                        updateFavourite={handleUpdateFavourite}
                        updatedData={infoUpdated}
                      />

                      {isTablet && (
                        <Keypad
                          dog={dog}
                          salePrice={salePrice}
                          dataApplication={dataApplication}
                          updatedData={infoUpdated}
                        />
                      )}
                    </InView>
                  </Suspense>

                  <Suspense
                    fallback={
                      <div className={styles.sectionLoading}>
                        <Loader theme="short" />
                      </div>
                    }
                  >
                    <HealthGuarantee application={dog.breeder.application} />
                    <Parents dog={infoUpdated} />
                    <Certificates dog={dog} isMobile={isMobile} />
                    <Shipment dog={dog} />
                    <Why />
                    <Breeder dog={dog} isMobile={isMobile} />
                    <AboutBreed breed={dog?.breed} breedDetails={breedDetails} />
                  </Suspense>
                </div>

                <div className={styles.rightPane}>
                  <Keypad
                    dog={dog}
                    showImage={!visibility.isPetImageVisible}
                    salePrice={salePrice}
                    dataApplication={dataApplication}
                    updatedData={infoUpdated}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.divider} />

        <Suspense
          fallback={
            <div className={styles.otherDogsLoading}>
              <Loader theme="tall" />
            </div>
          }
        >
          <OtherDogs
            breedId={dog?.breed?.id}
            breedDogs={breedDogs}
            favourites={favourites}
            updateFavourite={handleUpdateFavourite}
            isMobile={isMobile}
          />
        </Suspense>

        <Footer />

        <SignupModal
          isOpen={visibility.showSignup}
          close={() => setVisibility((prevState) => ({ ...prevState, showSignup: false }))}
          successRedirect={`/puppy/${dog.id}`}
          success={() => {
            setVisibility((prevState) => ({ ...prevState, showSignup: false }))
            refreshUserData()
            toggleFavourite(selectedDog)
          }}
        />

        {visibility.showFinancingModal ? (
          <FinancingModal
            setShowFinancingModal={() =>
              setVisibility((prevState) => ({
                ...prevState,
                showFinancingModal: false,
              }))
            }
          />
        ) : null}
      </main>
    </>
  )
}

export async function getStaticProps({ params }) {
  const apolloClient = initializeApollo()
  const notFound = { notFound: true }
  if (!params?.id) return notFound
  // Handle puppy select by id or name slug
  let variables

  if (isNaN(params?.id)) {
    variables = { slug: params?.id }
  } else {
    variables = { id: parseInt(params?.id) }
  }

  try {
    const { data } = await apolloClient.query({
      query: GET_DOG_DETAIL,
      variables: {
        selected: variables?.id,
        slug: variables?.slug,
      },
    })

    if (!isNaN(params?.id) && data?.dog?.slug) {
      return {
        redirect: {
          destination: `/puppy/${data?.dog?.slug}`,
          statusCode: 301,
        },
      }
    }

    if (data?.dog === null || !params) {
      return {
        redirect: {
          destination: '/search',
          statusCode: 301,
        },
      }
    }

    const [breedResult, breedPuppiesResult] = await Promise.all([
      apolloClient.query({
        fetchPolicy: 'no-cache',
        query: GET_BREED_DETAILS,
        variables: {
          id: data?.dog?.breed?.id,
          slug: data?.dog?.breed?.slug,
        },
      }),
      apolloClient.query({
        fetchPolicy: 'no-cache',
        query: PUPPY_SEARCH,
        variables: {
          breedSlug: data?.dog?.breed?.slug,
          limit: 20,
        },
      }),
    ])

    return {
      props: {
        dataDog: data,
        breedDetails: breedResult.data?.breed,
        allBreedPuppies: breedPuppiesResult.data,
      },
      revalidate: 60 * 60, // Revalidate at most once every hour
    }
  } catch (err) {
    console.error(err)
    throw err
  }
}
